import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Network, ArrowRight, Sparkles, Filter, Loader2, ChevronDown, Lock, Crown } from "lucide-react";

const SUBJECT_COLORS = {
  biology: "#10b981",
  biochemistry: "#8b5cf6",
  "gen-chem": "#f59e0b",
  orgo: "#ec4899",
  physics: "#3b82f6",
  psych: "#06b6d4",
  socio: "#ef4444",
};
const DISCIPLINE_COLOR = (label) => {
  const l = (label || "").toLowerCase();
  if (l.includes("biology")) return SUBJECT_COLORS.biology;
  if (l.includes("biochem")) return SUBJECT_COLORS.biochemistry;
  if (l.includes("gen chem")) return SUBJECT_COLORS["gen-chem"];
  if (l.includes("physics")) return SUBJECT_COLORS.physics;
  if (l.includes("psych")) return SUBJECT_COLORS.psych;
  return "var(--primary)";
};

const SUBJECT_FILTERS = [
  { id: "all", label: "All disciplines" },
  { id: "biology", label: "Biology" },
  { id: "biochemistry", label: "Biochemistry" },
  { id: "gen-chem", label: "Gen Chem" },
  { id: "physics", label: "Physics" },
  { id: "psych", label: "Psychology" },
];

export default function LinkIt() {
  const [themes, setThemes] = useState([]);
  const [planId, setPlanId] = useState("free");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/concept-map");
        setThemes(r.data.themes || []);
        setPlanId(r.data.plan_id || "free");
        // Auto-expand the free teaser theme so free users immediately see value
        const teaser = (r.data.themes || []).find((t) => !t.locked);
        if (teaser) setExpanded({ [teaser.id]: true });
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return themes;
    return themes.filter((t) => (t.subjects || []).includes(filter));
  }, [themes, filter]);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
  const lockedCount = themes.filter((t) => t.locked).length;
  const isFree = planId === "free";

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-8">
        <div className="pm-badge mb-3" style={{ background: "var(--accent)", color: "white" }}>
          <Network size={12} className="mr-1.5" /> Link It
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold mb-2">See how it all connects</h1>
        <p className="text-lg max-w-2xl" style={{ color: "var(--text-secondary)" }}>
          Every big MCAT idea lives in more than one subject. Here are the {themes.length} cross-discipline threads that most often show up on test day.
        </p>
      </div>

      {/* Free plan upgrade banner */}
      {isFree && lockedCount > 0 && (
        <div
          className="pm-card p-5 mb-6 flex items-start gap-3"
          style={{ background: "linear-gradient(135deg, var(--hl-yellow), var(--paper))", borderLeft: "4px solid var(--accent)" }}
          data-testid="linkit-upgrade-banner"
        >
          <Crown size={20} className="shrink-0 mt-0.5" style={{ color: "var(--accent)" }} />
          <div className="flex-1">
            <p className="font-display font-bold text-sm md:text-base">
              Your Free plan includes the first Link It theme. Upgrade to Plus to unlock all {lockedCount + 1} cross-discipline MCAT themes.
            </p>
            <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
              Every cross-discipline thread we've mapped for the MCAT, unlocked instantly. Cancel anytime.
            </p>
          </div>
          <Link
            to="/app/plans"
            className="pm-btn-accent text-xs md:text-sm inline-flex items-center gap-1.5 shrink-0"
            data-testid="linkit-upgrade-cta"
          >
            <Crown size={12} /> Upgrade
          </Link>
        </div>
      )}

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap mb-8" data-testid="linkit-filters">
        <Filter size={14} style={{ color: "var(--text-muted)" }} />
        {SUBJECT_FILTERS.map((s) => (
          <button
            key={s.id}
            onClick={() => setFilter(s.id)}
            className="text-xs px-3 py-1.5 rounded-full border transition-colors"
            style={{
              borderColor: filter === s.id ? "var(--accent)" : "var(--border)",
              background: filter === s.id ? "var(--accent)" : "var(--paper)",
              color: filter === s.id ? "white" : "var(--text-secondary)",
              fontWeight: filter === s.id ? 600 : 400,
            }}
            data-testid={`linkit-filter-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }} data-testid="linkit-loading">
          <Loader2 size={16} className="animate-spin" /> Wiring up the connections…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="pm-card p-8 text-center" data-testid="linkit-empty">
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>No themes match this filter yet.</p>
        </div>
      )}

      <div className="space-y-4">
        {filtered.map((t) => {
          const isOpen = expanded[t.id];
          if (t.locked) {
            return (
              <div key={t.id} className="pm-card overflow-hidden relative" data-testid={`linkit-theme-${t.id}`}>
                <div className="p-6 flex items-start gap-4">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                    style={{ background: "var(--muted)" }}
                  >
                    <Lock size={18} style={{ color: "var(--text-muted)" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <h2 className="font-display text-lg md:text-xl font-bold" style={{ color: "var(--text-muted)" }}>
                        {t.title}
                      </h2>
                      {(t.subjects || []).map((s) => (
                        <span
                          key={s}
                          className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold"
                          style={{ background: SUBJECT_COLORS[s] || "var(--muted)", color: "white", opacity: 0.6 }}
                        >
                          {s === "gen-chem" ? "Chem" : s === "psych" ? "Psych" : s.charAt(0).toUpperCase() + s.slice(1)}
                        </span>
                      ))}
                    </div>
                    <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{t.hook}</p>
                    <Link
                      to="/app/plans"
                      className="pm-btn-accent inline-flex items-center gap-1.5 text-xs mt-3"
                      data-testid={`linkit-unlock-${t.id}`}
                    >
                      <Crown size={12} /> Unlock this theme
                    </Link>
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div key={t.id} className="pm-card overflow-hidden" data-testid={`linkit-theme-${t.id}`}>
              <button
                onClick={() => toggle(t.id)}
                className="w-full text-left p-6 flex items-start gap-4 hover:bg-[var(--muted)] transition-colors"
                data-testid={`linkit-theme-toggle-${t.id}`}
              >
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ background: DISCIPLINE_COLOR(t.subjects[0]) }}
                >
                  <Sparkles size={20} color="white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <h2 className="font-display text-lg md:text-xl font-bold">{t.title}</h2>
                    {t.subjects.map((s) => (
                      <span
                        key={s}
                        className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold"
                        style={{ background: SUBJECT_COLORS[s] || "var(--muted)", color: "white" }}
                      >
                        {s === "gen-chem" ? "Chem" : s === "psych" ? "Psych" : s.charAt(0).toUpperCase() + s.slice(1)}
                      </span>
                    ))}
                  </div>
                  <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{t.hook}</p>
                </div>
                <ChevronDown
                  size={18}
                  style={{
                    color: "var(--text-muted)",
                    transform: isOpen ? "rotate(180deg)" : "rotate(0)",
                    transition: "transform 0.2s ease",
                  }}
                />
              </button>

              {isOpen && (
                <div className="border-t p-6 space-y-4" style={{ borderColor: "var(--border)" }} data-testid={`linkit-theme-body-${t.id}`}>
                  <div className="space-y-3">
                    {t.nodes.map((n, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-3 p-3 rounded-xl border"
                        style={{ borderColor: "var(--border)" }}
                        data-testid={`linkit-node-${t.id}-${i}`}
                      >
                        <span
                          className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold shrink-0 mt-1"
                          style={{ background: DISCIPLINE_COLOR(n.discipline), color: "white" }}
                        >
                          {n.discipline}
                        </span>
                        <div className="flex-1 min-w-0">
                          {n.topic_id ? (
                            <Link
                              to={`/app/topic/${n.topic_id}`}
                              className="font-medium text-sm hover:underline"
                              style={{ color: "var(--primary)" }}
                              data-testid={`linkit-node-link-${t.id}-${i}`}
                            >
                              {n.topic_name}
                            </Link>
                          ) : (
                            <span className="font-medium text-sm" style={{ color: "var(--text-muted)" }}>
                              {n.topic_name}
                            </span>
                          )}
                          <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{n.why}</p>
                        </div>
                        {n.analogy_ready && (
                          <span className="text-[10px]" style={{ color: "var(--mastery)" }}>✦ unlocked</span>
                        )}
                      </div>
                    ))}
                  </div>

                  <div
                    className="p-4 rounded-2xl flex items-start gap-3"
                    style={{ background: "var(--hl-yellow)" }}
                    data-testid={`linkit-takeaway-${t.id}`}
                  >
                    <ArrowRight size={16} className="shrink-0 mt-0.5" style={{ color: "var(--accent)" }} />
                    <p className="text-sm font-medium">{t.takeaway}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
